import { useEffect, useMemo, useState } from "react";
import { assetService } from "../services/assetService";
import { AssetCategoryOption } from "../types/assets";

// Session-lifetime cache — the same shape as userService's getUserBasic
// cache. Categories rarely change, and every Asset row/form on the page
// calling this hook would otherwise trigger its own fetch.
let cache: AssetCategoryOption[] | null = null;
let inflight: Promise<AssetCategoryOption[]> | null = null;

function fetchCategories(): Promise<AssetCategoryOption[]> {
    if (cache) return Promise.resolve(cache);
    if (!inflight) {
        inflight = assetService
            .getCategories()
            .then((data) => {
                cache = data;
                return data;
            })
            .finally(() => {
                inflight = null;
            });
    }
    return inflight;
}

// Fallback shown for a category code the fetched list doesn't recognize
// (e.g. an old asset whose category was since renamed/removed in Settings)
// — degrades to the raw code rather than crashing the row/form.
const unknownCategory = (code: string): AssetCategoryOption => ({
    code,
    label: code,
    life_hint: "",
    default_useful_life_months: 60,
});

export function useAssetCategories() {
    const [categories, setCategories] = useState<AssetCategoryOption[]>(
        cache || [],
    );
    const [loading, setLoading] = useState(!cache);
    const [error, setError] = useState("");

    useEffect(() => {
        if (cache) return;
        let cancelled = false;
        fetchCategories()
            .then((data) => {
                if (!cancelled) setCategories(data);
            })
            .catch(() => {
                if (!cancelled) setError("Failed to load asset categories");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const byCode = useMemo(
        () => Object.fromEntries(categories.map((c) => [c.code, c])),
        [categories],
    );

    const getCategoryMeta = (code: string): AssetCategoryOption =>
        byCode[code] || unknownCategory(code);

    return { categories, loading, error, getCategoryMeta };
}
